import React, { useState, useEffect, forwardRef } from 'react'
import { buildApiClient } from '../../helpers/client'
import { addDays, subDays, format } from 'date-fns'
import DatePicker from 'react-datepicker'
import "./index.css"
import StaffMember from './components/StaffMember'
import BookingPreview from './components/BookingPreview'
import UtilizationCell from './components/UtilizationCell'
import UnassignedCell from './components/UnassignedCell'
import AssignStaffModal from './components/AssignStaffModal'
import Pager from '../ui/Pager'
import sortStaff from './helpers/sort_staff'

const per = 20

const StaffForDate = ({ apiKey, dateFormat, startDate, daysToShow }) => {
  const client = buildApiClient(apiKey)

  const [ready, setReady] = useState(false)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [activeDate, setActiveDate] = useState(() => {
    if (startDate) {
      // Parse date string as local date to avoid timezone shifts
      if (typeof startDate === 'string') {
        const [year, month, day] = startDate.split('-').map(Number)
        return new Date(year, month - 1, day) // month is 0-indexed
      }
      return new Date(startDate)
    }
    return new Date()
  })
  const [activeEventBooking, setActiveEventBooking] = useState(null)
  const [staffMembers, setStaffMembers ] = useState([])
  const [totalPages, setTotalPages] = useState(1)
  const [staffBlockoutDates, setStaffBlockoutDates] = useState([])
  const [eventBookings, setEventBookings] = useState([])

  const activeStartDate = activeDate
  const activeEndDate = addDays(activeStartDate, daysToShow - 1)
  
  // Generate array of dates for the range
  const daysInRange = Array.from({ length: daysToShow }, (_, index) => 
    addDays(activeStartDate, index)
  )


  useEffect(() => {
    loadStaff()
  }, [page])

  useEffect(() => {
    loadDataForRange()
  }, [activeDate])

  async function loadStaff() {
    const url = `/api/v1/users?role=staff&per=${per}&page=${page}`
    const response = await client.get(url)
    const staffMembers = response.data.data
    const totalResults = response.data.meta.totalCount
    setTotalPages(Math.ceil(totalResults / per))
    setStaffMembers(sortStaff(staffMembers))
    setReady(true)
  }

  async function loadDataForRange() {
    setLoading(true)
    await loadBookingsForRange()
    await loadStaffBlockoutDatesForRange()
    setLoading(false)
  }

  async function loadBookingsForRange() {
    const url = `/api/v1/event_bookings?event_status=any_confirmed&event_date_start=${format(activeStartDate, 'yyyy-MM-dd')}&event_date_end=${format(activeEndDate, 'yyyy-MM-dd')}`
    const response = await client.get(url)
    const eventBookings = response.data.data
    setEventBookings(eventBookings)
  }

  async function loadStaffBlockoutDatesForRange() {
    const url = `/api/v1/staff_blockout_date_ranges?start_date=${format(activeStartDate, 'yyyy-MM-dd')}&end_date=${format(activeEndDate, 'yyyy-MM-dd')}`
    const response = await client.get(url)
    const staffBlockoutDates = response.data.data
    setStaffBlockoutDates(staffBlockoutDates)
  }

  function setPreviousRange(e) {
    e.preventDefault()
    setActiveDate(subDays(activeDate, daysToShow))
  }

  function setNextRange(e) {
    e.preventDefault()
    setActiveDate(addDays(activeDate, daysToShow))
  }

  async function handleStaffAssignedToBooking({ staffMemberId, staffMemberRole, bookingId }) {
    setLoading(true)
    try {
      const response = await client.post(`/api/v1/events/${bookingId}/event_staff_members`, {
        staff_id: staffMemberId,
        role: staffMemberRole
      })
      await loadBookingsForRange()
      setActiveEventBooking(null)
    }
    catch(e) {
      console.error("Error assigning staff member to booking", e)
    }
    setLoading(false)
  }

  if(!ready) {
    return null
  }

  const Loader = () => {
    if(loading) {
      return (
        <span className="ms-2">
          <i className="fa fa-spinner fa-spin" />
        </span>
      )
    }
    return null
  }

  const TitleInput = forwardRef(({ value, onClick }, ref) => {
    function handleClick(e) {
      e.preventDefault()
      onClick(e)
    }

    return (
      <a href="#" onClick={handleClick} ref={ref}>
        <h5>Staffing: { format(activeStartDate, dateFormat) } to { format(activeEndDate, dateFormat) } <Loader /></h5>
      </a>
    )
  });

  return (
    <div className="widget__staff-utilization-for-date">
      <AssignStaffModal
        client={client}
        apiKey={apiKey}
        activeEventBooking={activeEventBooking}
        onCancel={() => setActiveEventBooking(null)}
        handleStaffAssignedToBooking={handleStaffAssignedToBooking}
      />
      <div className="d-flex align-items-baseline">
        <a href="#" onClick={setPreviousRange} className="me-2 subtle-edit">
          <i className="fa fa-chevron-left" />
        </a>
        <DatePicker
          name={name}
          selectsRange
          customInput={<TitleInput />}
          onChange={setActiveDate}
          selected={activeDate}
          dateFormat={dateFormat}
          startDate={activeStartDate}
          endDate={activeEndDate}
        />
        <a href="#" onClick={setNextRange} className="ms-2 subtle-edit">
          <i className="fa fa-chevron-right" />
        </a>
      </div>

      <table className="table table-bordered table-sm mt-3">
        <thead>
          <tr>
            <th>Staff Member</th>
            { daysInRange.map(date => (
              <th key={format(date, 'yyyy-MM-dd')}>{ format(date, 'EEE') }</th>
            ))}
          </tr>
        </thead>
        <tbody>
          { staffMembers.map(staffMember => (
            <tr key={staffMember.id}>
              <td>
                <StaffMember staffMember={staffMember} />
              </td>
              { daysInRange.map(date => (
                <UtilizationCell
                  key={`${staffMember.id}-${format(date, 'yyyy-MM-dd')}`}
                  date={date}
                  staffMember={staffMember}
                  eventBookings={eventBookings}
                  staffBlockoutDates={staffBlockoutDates}
                  onClickBooking={booking => setActiveEventBooking(booking)}
                />
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Unassigned</td>
            { daysInRange.map(date => (
              <UnassignedCell
                key={format(date, 'yyyy-MM-dd')}
                date={date}
                eventBookings={eventBookings}
                onClickBooking={booking => setActiveEventBooking(booking)}
              />
            ))}
          </tr>
        </tfoot>
      </table>
      <Pager
        page={page}
        per={per}
        handleChange={newPage => setPage(newPage)}
        totalPages={totalPages}
      />
    </div>
  )
}

export default StaffForDate
